import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';

import { useAuth } from '@/lib/auth';
import { queryPackage, type QueryProgressEvent } from '../api';
import { useLiveElapsed } from '../hooks';
import { QueryResultCard, type QueryHistoryItem } from '../components/QueryResultCard';
import { VersionSwitcher } from '../components/VersionSwitcher';
import { navigateTo } from '../router';
import type { LcRouteParams } from '../types';
import {
  buildIndexTreeRoute,
  buildQueryRoute,
  displayPackageName,
  formatAskElapsed,
} from '../utils';

interface QueryPageProps {
  params: LcRouteParams;
  isAdmin?: boolean;
}

/** One-line human narration of a streamed pipeline stage. */
function describeProgress(event: QueryProgressEvent): string {
  switch (event.stage) {
    case 'planning':
      return 'Planning sub-questions…';
    case 'planned': {
      const n = event.subQuestions?.length ?? 0;
      if (n === 0) return 'Planned — searching…';
      const pkgs = [
        ...new Set(
          (event.subQuestions ?? [])
            .map((q) => (q.version ? `${q.package}@${q.version}` : q.package))
            .filter(Boolean)
        ),
      ];
      return `Split into ${n} sub-question${n === 1 ? '' : 's'}: ${pkgs.join(', ')}`;
    }
    case 'retrieving':
      return event.total && event.total > 1
        ? `Searching ${event.package} (${event.index}/${event.total})…`
        : `Searching ${event.package}…`;
    case 'retrieved':
      return `Retrieved ${event.package} — ${event.chunkCount ?? 0} chunks`;
    case 'synthesizing':
      return 'Synthesizing answer from retrieved chunks…';
    default:
      return 'Working…';
  }
}

export function QueryPage({ params, isAdmin }: QueryPageProps) {
  const { deploymentMode } = useAuth();
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressLines, setProgressLines] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const askElapsedMs = useLiveElapsed(isSubmitting);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!question.trim()) {
      return;
    }

    setIsSubmitting(true);
    setProgressLines([]);
    setError(null);

    try {
      const result = await queryPackage({
        question: question.trim(),
        registry: params.registry,
        name: params.name,
        version: params.version,
        onProgress: (progressEvent) => {
          setProgressLines((previous) => [...previous, describeProgress(progressEvent)]);
        },
      });

      setHistory((previous) => [
        {
          question: question.trim(),
          result,
        },
        ...previous,
      ]);
      setQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to query package.');
    } finally {
      setIsSubmitting(false);
      setProgressLines([]);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">
            {displayPackageName(params.registry, params.name)}
          </h2>
          <div className="mt-1">
            <VersionSwitcher params={params} buildRoute={buildQueryRoute} />
          </div>
        </div>
        {isAdmin && deploymentMode === 'saas' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigateTo(buildIndexTreeRoute(params))}
          >
            Browse index
          </Button>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="lc-surface overflow-hidden border border-rule p-4"
        data-testid="query-form"
      >
        <p className="mb-3 text-xs leading-relaxed text-dim">
          Ask one focused question. Retrieval is pinned to{' '}
          <code className="font-mono text-link">
            {params.registry}:{params.name}@{params.version}
          </code>
          , not the newest release or the model&apos;s memory.
        </p>
        <label htmlFor="lc-question" className="sr-only">
          Ask a question
        </label>
        <div className="flex gap-2">
          <input
            id="lc-question"
            data-testid="query-input"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder={`How does ${displayPackageName(params.registry, params.name)}@${params.version}…`}
            className="lc-input flex-1"
            disabled={isSubmitting}
          />
          <Button type="submit" size="sm" disabled={isSubmitting || !question.trim()}>
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <svg
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Asking…
                {askElapsedMs != null && (
                  <span className="font-mono text-2xs opacity-80">
                    {formatAskElapsed(askElapsedMs)}
                  </span>
                )}
              </span>
            ) : (
              'Ask'
            )}
          </Button>
        </div>
        {isSubmitting && progressLines.length > 0 && (
          <div
            className="mt-3 space-y-1 border-t border-rule-soft pt-3"
            data-testid="query-progress"
            aria-live="polite"
          >
            {progressLines.map((line, i) => (
              <p
                key={`${i}-${line}`}
                className="flex items-center gap-1.5 font-mono text-2xs text-dim"
              >
                <span
                  aria-hidden
                  className={i === progressLines.length - 1 ? 'text-accent' : 'text-good'}
                >
                  {i === progressLines.length - 1 ? '▸' : '✓'}
                </span>
                {line}
              </p>
            ))}
          </div>
        )}
      </form>

      <div className="space-y-3">
        {history.map((item, index) => (
          <QueryResultCard key={`${item.question}-${index}`} item={item} />
        ))}
      </div>

      {error && (
        <ErrorDialog
          title="Unable to Query Package"
          message={error}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
}
